/**
 * Inspection-duration ladders, shared by the API (which resolves durations for
 * the scheduling task feed) and the UI (whose calculator must show exactly what
 * the feed would send — so both sides must use this one resolver).
 *
 * A ladder is a list of buckets for a single inspection type, each carrying an
 * inclusive upper bound on the order's quantity. The bounds alone define the
 * ranges: a bucket covers everything above the previous bucket's bound up to and
 * including its own. `maxQuantity: null` means unbounded (a flat duration for any
 * quantity) and only makes sense as the last bucket.
 *
 * Because ranges are derived from the bounds rather than entered by hand, a
 * ladder cannot contain a gap or an overlap. The only ways to break one are to
 * repeat a bound or to put anything after an unbounded bucket, which leaves a
 * bucket unreachable — that is what `validateLadder` reports.
 *
 * A quantity above the last bound resolves to no duration at all; that is the
 * intended way to say "not configured", and the feed omits durationMinutes.
 */

/**
 * The loose shape both the `DurationBucket` entity and its DTO satisfy — the DTO
 * marks every field optional because a create sends a partial. Treat a missing
 * `maxQuantity` exactly like an explicit null (unbounded).
 */
export interface DurationBucketLike {
  /** Upper quantity bound (inclusive); null/absent = unbounded. */
  maxQuantity?: number | null;
  minutes?: number;
}

/** A bucket's derived range, for display. `to: null` = unbounded. */
export interface LadderRange {
  from: number;
  to: number | null;
  minutes: number;
}

export type LadderIssue =
  | { kind: 'duplicateBound'; bound: number }
  | { kind: 'multipleUnbounded' }
  | { kind: 'boundAfterUnbounded' }
  | { kind: 'invalidBound'; bound: number }
  | { kind: 'invalidMinutes'; minutes: number };

/** Ascending by bound, with the unbounded bucket (if any) last. */
export function sortLadder<T extends DurationBucketLike>(buckets: T[]): T[] {
  return [...buckets].sort(
    (a, b) => (a.maxQuantity ?? Infinity) - (b.maxQuantity ?? Infinity)
  );
}

/**
 * Resolve the duration (minutes) for a quantity, or `undefined` when no bucket
 * covers it. Buckets need not be sorted.
 */
export function resolveDurationMinutes(
  buckets: DurationBucketLike[] | undefined,
  quantity: number | undefined
): number | undefined {
  if (!buckets?.length) {
    return undefined;
  }

  const q = quantity || 0;
  const bucket = sortLadder(buckets).find(
    (b) => b.maxQuantity == null || q <= b.maxQuantity
  );
  return bucket?.minutes;
}

/** Derived display ranges, sorted. */
export function toRanges(buckets: DurationBucketLike[]): LadderRange[] {
  const sorted = sortLadder(buckets);
  return sorted.map((bucket, i) => ({
    from: i === 0 ? 0 : sorted[i - 1].maxQuantity ?? 0,
    to: bucket.maxQuantity,
    minutes: bucket.minutes
  }));
}

/**
 * The quantity above which the ladder stops answering, or null when it covers
 * everything (i.e. it ends in an unbounded bucket). Drives the explicit
 * "no duration beyond here" row.
 */
export function uncoveredAbove(buckets: DurationBucketLike[]): number | null {
  if (!buckets.length) {
    return 0;
  }
  const sorted = sortLadder(buckets);
  return sorted[sorted.length - 1].maxQuantity;
}

/** Everything wrong with a ladder. Empty array = valid. */
export function validateLadder(buckets: DurationBucketLike[]): LadderIssue[] {
  const issues: LadderIssue[] = [];
  const seen = new Set<number>();
  let unbounded = 0;

  for (const bucket of buckets) {
    if (bucket.maxQuantity == null) {
      unbounded++;
    } else if (
      !Number.isInteger(bucket.maxQuantity) ||
      bucket.maxQuantity < 1
    ) {
      issues.push({ kind: 'invalidBound', bound: bucket.maxQuantity });
    } else if (seen.has(bucket.maxQuantity)) {
      issues.push({ kind: 'duplicateBound', bound: bucket.maxQuantity });
    } else {
      seen.add(bucket.maxQuantity);
    }

    if (!Number.isInteger(bucket.minutes) || bucket.minutes < 1) {
      issues.push({ kind: 'invalidMinutes', minutes: bucket.minutes });
    }
  }

  if (unbounded > 1) {
    issues.push({ kind: 'multipleUnbounded' });
  } else if (unbounded === 1 && seen.size) {
    // One unbounded bucket is only reachable when nothing is bounded: sorted
    // last, it would answer every quantity the bounded buckets did not — which
    // is fine — but a bounded bucket *after* it can never be reached.
    const sorted = sortLadder(buckets);
    if (sorted[sorted.length - 1].maxQuantity != null) {
      issues.push({ kind: 'boundAfterUnbounded' });
    }
  }

  return issues;
}

/**
 * Whether adding/changing a bucket to this bound would collide with an existing
 * one. `excludeId` skips the bucket being edited.
 */
export function boundTaken<T extends DurationBucketLike & { id?: number }>(
  buckets: T[],
  bound: number | null,
  excludeId?: number
): boolean {
  return buckets.some(
    (b) =>
      (excludeId == null || b.id !== excludeId) &&
      (b.maxQuantity ?? null) === (bound ?? null)
  );
}
